import { useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { SearchInput } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import {
  useCatalogItems,
  useCatalogCategories,
  useCreateCatalogItem,
  useUpdateCatalogItem,
  useDeleteCatalogItem,
} from '@/hooks/useCatalog'
import type {
  CatalogItem,
  CatalogItemCreate,
  CatalogItemUpdate,
  CatalogItemType,
  BillingType,
  BillingCycle,
} from '@/types'
import {
  Plus, Edit2, Trash2, X, AlertTriangle, Package, Loader2,
} from 'lucide-react'

// ==========================================
// Constants
// ==========================================

const TYPE_LABELS: Record<CatalogItemType, string> = {
  product: 'Produto',
  service: 'Servico',
}

const BILLING_LABELS: Record<BillingType, string> = {
  one_time: 'Avulso',
  recurring: 'Recorrente',
}

const CYCLE_LABELS: Record<BillingCycle, string> = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
}

const TYPE_BADGE: Record<CatalogItemType, 'default' | 'info'> = {
  product: 'default',
  service: 'info',
}

const BILLING_BADGE: Record<BillingType, 'secondary' | 'warning'> = {
  one_time: 'secondary',
  recurring: 'warning',
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: unknown } } }
  const detail = axiosErr?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((d: Record<string, string>) => d.msg).join('; ')
  return fallback
}

const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'

// ==========================================
// Main Page
// ==========================================


const COUNTRIES: { code: string; label: string; flag: string; currency: string }[] = [
  { code: 'BR',  label: 'Brasil',     flag: '🇧🇷', currency: 'R$'   },
  { code: 'AR',  label: 'Argentina',  flag: '🇦🇷', currency: 'AR$'  },
  { code: 'CL',  label: 'Chile',      flag: '🇨🇱', currency: 'CLP$' },
  { code: 'USD', label: 'Dólar (USD)', flag: '💵', currency: 'US$'  },
  { code: 'UY',  label: 'Uruguai',    flag: '🇺🇾', currency: 'U$'   },
  { code: 'PE',  label: 'Peru',       flag: '🇵🇪', currency: 'S/.'  },
  { code: 'CO',  label: 'Colômbia',   flag: '🇨🇴', currency: 'COP$' },
  { code: 'PY',  label: 'Paraguai',   flag: '🇵🇾', currency: '₲'    },
  { code: 'MX',  label: 'México',     flag: '🇲🇽', currency: 'MX$'  },
  { code: 'BO',  label: 'Bolívia',    flag: '🇧🇴', currency: 'Bs.'  },
]

function fmtMoney(value: number | null | undefined, currency = 'R$'): string {
  if (value == null) return '—'
  return `${currency} ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}


export function CatalogPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState<CatalogItemType | ''>('')
  const [filterBilling, setFilterBilling] = useState<BillingType | ''>('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | null>(null)

  const [showCreate, setShowCreate] = useState(false)
  const [editItem, setEditItem] = useState<CatalogItem | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<CatalogItem | null>(null)

  const { data: categories } = useCatalogCategories()
  const { data, isLoading } = useCatalogItems({
    page,
    per_page: 20,
    search: search || undefined,
    item_type: filterType || undefined,
    billing_type: filterBilling || undefined,
    category: filterCategory || undefined,
    is_active: filterActive,
  })

  const items = data?.items ?? []
  const totalPages = data?.pages ?? 1

  const columns: DataGridColumn<CatalogItem>[] = [
    {
      id: 'code',
      header: 'Codigo',
      accessor: (r) => <span className="font-mono text-xs">{r.code}</span>,
      sortValue: (r) => r.code,
      filterable: true,
      filterValue: (r) => r.code,
    },
    {
      id: 'name',
      header: 'Nome',
      accessor: (r) => <span className="font-medium">{r.name}</span>,
      sortValue: (r) => r.name,
      filterable: true,
      filterValue: (r) => r.name,
    },
    {
      id: 'item_type',
      header: 'Tipo',
      accessor: (r) => <Badge variant={TYPE_BADGE[r.item_type]}>{TYPE_LABELS[r.item_type]}</Badge>,
      sortValue: (r) => r.item_type,
    },
    {
      id: 'billing_type',
      header: 'Cobranca',
      accessor: (r) => <Badge variant={BILLING_BADGE[r.billing_type]}>{BILLING_LABELS[r.billing_type]}</Badge>,
      sortValue: (r) => r.billing_type,
    },
    {
      id: 'billing_cycle',
      header: 'Ciclo',
      accessor: (r) => r.billing_cycle ? CYCLE_LABELS[r.billing_cycle] : '-',
      sortValue: (r) => r.billing_cycle ?? '',
    },
    {
      id: 'category',
      header: 'Categoria',
      accessor: (r) => r.category ?? '-',
      sortValue: (r) => r.category ?? '',
      filterable: true,
      filterValue: (r) => r.category ?? '',
    },
    {
      id: 'prices',
      header: 'Preços',
      accessor: (r) => {
        const codes = r.prices ? Object.keys(r.prices) : []
        if (codes.length === 0) return <span className="text-xs text-muted-foreground italic">—</span>
        const brPrice = r.prices?.['BR']
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            {brPrice != null && (
              <span className="font-mono text-xs">{fmtMoney(brPrice)}</span>
            )}
            <span className="text-xs text-muted-foreground">
              {codes.map(c => {
                const country = COUNTRIES.find(co => co.code === c)
                return country ? country.flag : c
              }).join(' ')}
            </span>
            <span className="text-[10px] text-muted-foreground">({codes.length})</span>
          </div>
        )
      },
    },
    {
      id: 'max_disc',
      header: 'Desc. máx',
      accessor: (r) => {
        const v = r.max_discount_percent
        if (v == null || v === 0) return <span className="text-xs text-muted-foreground">—</span>
        return <span className="text-xs font-mono">{Number(v).toFixed(1)}%</span>
      },
    },
    {
      id: 'goal',
      header: 'Meta anual',
      accessor: (r) => {
        const v = r.sales_goal_value_annual_brl
        if (v == null) return <span className="text-xs text-muted-foreground">—</span>
        return (
          <div className="text-xs">
            <div className="font-mono">{fmtMoney(v)}</div>
            {r.sales_goal_year != null && <div className="text-[10px] text-muted-foreground">{r.sales_goal_year}</div>}
          </div>
        )
      },
    },
    {
      id: 'unit',
      header: 'Unidade',
      accessor: (r) => r.unit ?? '-',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (r) => (
        <Badge variant={r.is_active ? 'success' : 'secondary'}>
          {r.is_active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
      sortValue: (r) => r.is_active ? 1 : 0,
    },
    {
      id: 'actions',
      header: 'Acoes',
      accessor: (r) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditItem(r) }}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(r) }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
      sortable: false,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Produtos & Servicos</h1>
          <p className="text-muted-foreground">Gerencie o catalogo de produtos e servicos</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Item
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <SearchInput
                placeholder="Buscar por codigo ou nome..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              />
            </div>
            <select
              className={selectClass + ' w-[150px]'}
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value as CatalogItemType | ''); setPage(1) }}
            >
              <option value="">Todos os tipos</option>
              <option value="product">Produto</option>
              <option value="service">Servico</option>
            </select>
            <select
              className={selectClass + ' w-[160px]'}
              value={filterBilling}
              onChange={(e) => { setFilterBilling(e.target.value as BillingType | ''); setPage(1) }}
            >
              <option value="">Todas cobrancas</option>
              <option value="one_time">Avulso</option>
              <option value="recurring">Recorrente</option>
            </select>
            <select
              className={selectClass + ' w-[160px]'}
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setPage(1) }}
            >
              <option value="">Todas categorias</option>
              {(categories ?? []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              className={selectClass + ' w-[130px]'}
              value={filterActive === null ? '' : filterActive ? '1' : '0'}
              onChange={(e) => {
                const v = e.target.value
                setFilterActive(v === '' ? null : v === '1')
                setPage(1)
              }}
            >
              <option value="">Todos</option>
              <option value="1">Ativos</option>
              <option value="0">Inativos</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card>
        <CardContent className="p-0">
          <DataGrid
            gridId="catalog-items"
            columns={columns}
            data={items}
            isLoading={isLoading}
            emptyMessage="Nenhum item encontrado"
            emptyIcon={<Package className="h-12 w-12 text-muted-foreground" />}
          />
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Pagina {page} de {totalPages} ({data?.total ?? 0} itens)
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Proxima
          </Button>
        </div>
      )}

      {/* Modals */}
      {showCreate && <CatalogFormPanel onClose={() => setShowCreate(false)} categories={categories ?? []} />}
      {editItem && <CatalogFormPanel item={editItem} onClose={() => setEditItem(null)} categories={categories ?? []} />}
      {deleteConfirm && <DeleteItemModal item={deleteConfirm} onClose={() => setDeleteConfirm(null)} />}
    </div>
  )
}

// ==========================================
// Form Panel (Slide-over)
// ==========================================

function CatalogFormPanel({
  item,
  onClose,
  categories,
}: {
  item?: CatalogItem
  onClose: () => void
  categories: string[]
}) {
  const isEdit = !!item
  const createMutation = useCreateCatalogItem()
  const updateMutation = useUpdateCatalogItem()

  const [code, setCode] = useState(item?.code ?? '')
  const [name, setName] = useState(item?.name ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [descriptionRich, setDescriptionRich] = useState(item?.description_rich ?? '')
  const [itemType, setItemType] = useState<CatalogItemType>(item?.item_type ?? 'product')
  const [billingType, setBillingType] = useState<BillingType>(item?.billing_type ?? 'one_time')
  const [billingCycle, setBillingCycle] = useState<BillingCycle | ''>(item?.billing_cycle ?? '')
  const [category, setCategory] = useState(item?.category ?? '')
  const [unit, setUnit] = useState(item?.unit ?? '')
  const [isActive, setIsActive] = useState(item?.is_active ?? true)
  const [prices, setPrices] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    for (const c of COUNTRIES) {
      const v = item?.prices?.[c.code]
      out[c.code] = v != null ? String(v) : ''
    }
    return out
  })
  const [maxDiscount, setMaxDiscount] = useState<string>(item?.max_discount_percent != null ? String(item.max_discount_percent) : '')
  const [salesGoal, setSalesGoal] = useState<string>(item?.sales_goal_value_annual_brl != null ? String(item.sales_goal_value_annual_brl) : '')
  const [salesGoalYear, setSalesGoalYear] = useState<string>(item?.sales_goal_year != null ? String(item.sales_goal_year) : String(new Date().getFullYear()))
  const [error, setError] = useState('')
  const currentUser = useAuthStore((st) => st.user)
  const userRoleName = currentUser?.role?.name || ''
  const canEditGoal = ['sales_manager', 'cfo', 'ceo'].includes(userRoleName)

  const saving = createMutation.isPending || updateMutation.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!code.trim() || !name.trim()) {
      setError('Codigo e Nome sao obrigatorios')
      return
    }

    try {
      if (isEdit && item) {
        const pricesObj: Record<string, number> = {}
        for (const c of COUNTRIES) {
          const v = prices[c.code]
          if (v && !isNaN(parseFloat(v))) pricesObj[c.code] = parseFloat(v)
        }
        const payload: CatalogItemUpdate = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || null,
          description_rich: descriptionRich.trim() || null,
          item_type: itemType,
          billing_type: billingType,
          billing_cycle: billingType === 'recurring' && billingCycle ? billingCycle : null,
          category: category.trim() || null,
          unit: unit.trim() || null,
          is_active: isActive,
          prices: Object.keys(pricesObj).length ? pricesObj : null,
          max_discount_percent: maxDiscount ? parseFloat(maxDiscount) : null,
        }
        if (canEditGoal) {
          payload.sales_goal_value_annual_brl = salesGoal ? parseFloat(salesGoal) : null
          payload.sales_goal_year = salesGoalYear ? parseInt(salesGoalYear, 10) : null
        }
        await updateMutation.mutateAsync({ id: item.id, data: payload })
      } else {
        const pricesObj: Record<string, number> = {}
        for (const c of COUNTRIES) {
          const v = prices[c.code]
          if (v && !isNaN(parseFloat(v))) pricesObj[c.code] = parseFloat(v)
        }
        const payload: CatalogItemCreate = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || null,
          description_rich: descriptionRich.trim() || null,
          item_type: itemType,
          billing_type: billingType,
          billing_cycle: billingType === 'recurring' && billingCycle ? billingCycle : null,
          category: category.trim() || null,
          unit: unit.trim() || null,
          prices: Object.keys(pricesObj).length ? pricesObj : null,
          max_discount_percent: maxDiscount ? parseFloat(maxDiscount) : null,
        }
        if (canEditGoal) {
          payload.sales_goal_value_annual_brl = salesGoal ? parseFloat(salesGoal) : null
          payload.sales_goal_year = salesGoalYear ? parseInt(salesGoalYear, 10) : null
        }
        await createMutation.mutateAsync(payload)
      }
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar item'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="bg-card w-full max-w-lg h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-card border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar Item' : 'Novo Item'}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="text-sm font-medium">Codigo *</label>
            <input className={selectClass + ' mt-1'} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Ex: PROD-001" />
          </div>

          <div>
            <label className="text-sm font-medium">Nome *</label>
            <input className={selectClass + ' mt-1'} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do produto ou servico" />
          </div>

          <div>
            <label className="text-sm font-medium">Descricao</label>
            <textarea
              className={selectClass + ' mt-1 min-h-[80px]'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descricao opcional"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Tipo *</label>
              <select className={selectClass + ' mt-1'} value={itemType} onChange={(e) => setItemType(e.target.value as CatalogItemType)}>
                <option value="product">Produto</option>
                <option value="service">Servico</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">Tipo de Cobranca *</label>
              <select className={selectClass + ' mt-1'} value={billingType} onChange={(e) => setBillingType(e.target.value as BillingType)}>
                <option value="one_time">Avulso</option>
                <option value="recurring">Recorrente</option>
              </select>
            </div>
          </div>

          {billingType === 'recurring' && (
            <div>
              <label className="text-sm font-medium">Ciclo de Cobranca</label>
              <select className={selectClass + ' mt-1'} value={billingCycle} onChange={(e) => setBillingCycle(e.target.value as BillingCycle | '')}>
                <option value="">Selecione...</option>
                <option value="monthly">Mensal</option>
                <option value="quarterly">Trimestral</option>
                <option value="semiannual">Semestral</option>
                <option value="annual">Anual</option>
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Categoria</label>
              <input
                className={selectClass + ' mt-1'}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Ex: Software"
                list="catalog-categories"
              />
              <datalist id="catalog-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>

            <div>
              <label className="text-sm font-medium">Unidade</label>
              <input className={selectClass + ' mt-1'} value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Ex: licenca, hora" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Descrição detalhada (rich)</label>
            <textarea
              className={selectClass + ' mt-1 min-h-[120px]'}
              value={descriptionRich}
              onChange={(e) => setDescriptionRich(e.target.value)}
              placeholder="Descrição completa para apresentação em propostas (Markdown ou texto livre)"
            />
          </div>

          {/* === Preços por região === */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold">Preços por região</label>
              <span className="text-xs text-muted-foreground">Deixe em branco se não comercializa naquele país</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {COUNTRIES.map((c) => (
                <div key={c.code}>
                  <label className="text-xs text-muted-foreground flex items-center gap-1">
                    <span>{c.flag}</span> {c.label}
                  </label>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-xs text-muted-foreground w-10 flex-shrink-0">{c.currency}</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={selectClass + ' text-sm flex-1'}
                      value={prices[c.code] || ''}
                      onChange={(e) => setPrices({ ...prices, [c.code]: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* === Controles comerciais === */}
          <div className="space-y-3 pt-2 border-t">
            <label className="text-sm font-semibold">Controles comerciais</label>
            <div>
              <label className="text-xs text-muted-foreground">Desconto máximo sem aprovação (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className={selectClass + ' mt-1'}
                value={maxDiscount}
                onChange={(e) => setMaxDiscount(e.target.value)}
                placeholder="Ex: 10 → descontos até 10% não precisam de aprovação CFO/CEO"
              />
            </div>

            {canEditGoal ? (
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground">Meta anual (R$) <span className="text-primary">[Gestor/CFO/CEO]</span></label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={selectClass + ' mt-1'}
                    value={salesGoal}
                    onChange={(e) => setSalesGoal(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Ano</label>
                  <input
                    type="number"
                    min="2020"
                    max="2050"
                    className={selectClass + ' mt-1'}
                    value={salesGoalYear}
                    onChange={(e) => setSalesGoalYear(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div className="text-xs p-2 rounded bg-muted/40 text-muted-foreground">
                💡 A <strong>meta anual</strong> deste produto é {item?.sales_goal_value_annual_brl ? fmtMoney(item.sales_goal_value_annual_brl) : '—'}{item?.sales_goal_year ? ` (${item.sales_goal_year})` : ''}. Apenas Gestor Comercial, CFO ou CEO podem alterar.
              </div>
            )}
          </div>

          {isEdit && (
            <div className="flex items-center gap-3 pt-2 border-t">
              <label className="text-sm font-medium">Ativo</label>
              <button
                type="button"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isActive ? 'bg-primary' : 'bg-muted'}`}
                onClick={() => setIsActive(!isActive)}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Salvar' : 'Criar Item'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ==========================================
// Delete Modal
// ==========================================

function DeleteItemModal({ item, onClose }: { item: CatalogItem; onClose: () => void }) {
  const mutation = useDeleteCatalogItem()
  const [error, setError] = useState('')

  async function handleDelete() {
    setError('')
    try {
      await mutation.mutateAsync(item.id)
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao excluir item'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card rounded-lg shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full bg-destructive/10">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <h3 className="text-lg font-semibold">Excluir Item</h3>
        </div>
        <p className="text-muted-foreground mb-1">
          Tem certeza que deseja excluir o item <strong>{item.name}</strong> ({item.code})?
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          O item sera desativado e nao aparecera mais no catalogo.
        </p>
        {error && (
          <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm mb-4">{error}</div>
        )}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Excluir
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CatalogPage
